import React from 'react';
import {
  ArrowTrendingUpIcon,
  UserGroupIcon,
  RocketLaunchIcon,
  ShieldCheckIcon,
  LightBulbIcon,
  CheckBadgeIcon,
  GlobeAltIcon,
  ChartBarIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import { useTranslation, Trans } from 'react-i18next';

const Performance = () => {
  const { t } = useTranslation();

  const contributions = [
    {
      icon: UserGroupIcon,
      title: t('performance.contributions.employment.title'),
      description: t('performance.contributions.employment.description'),
      stat: "20+",
      color: "bg-blue-500"
    },
    {
      icon: LightBulbIcon,
      title: t('performance.contributions.innovation.title'),
      description: t('performance.contributions.innovation.description'),
      stat: "7+",
      color: "bg-yellow-500"
    },
    {
      icon: GlobeAltIcon,
      title: t('performance.contributions.digital.title'),
      description: t('performance.contributions.digital.description'),
      stat: "100%",
      color: "bg-csl-green"
    },
    {
      icon: ChartBarIcon,
      title: t('performance.contributions.growth.title'),
      description: t('performance.contributions.growth.description'),
      stat: "25%",
      color: "bg-purple-500"
    }
  ];

  const reasons = [
    {
      icon: ShieldCheckIcon,
      title: t('performance.reasons.expertise.title'),
      description: t('performance.reasons.expertise.description')
    },
    {
      icon: UserGroupIcon,
      title: t('performance.reasons.approach.title'),
      description: t('performance.reasons.approach.description')
    },
    {
      icon: ClockIcon,
      title: t('performance.reasons.support.title'),
      description: t('performance.reasons.support.description')
    }
  ];

  return (
    <section id="performance" className="py-12 sm:py-16 lg:py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12 sm:mb-16 animate-slide-up">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6">
            <Trans i18nKey="performance.title">
              Notre <span className="text-csl-green">Performance</span>
            </Trans>
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto px-4">
            {t('performance.description')}
          </p>
        </div>

        {/* Contributions Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8 mb-16 sm:mb-20">
          {contributions.map((item, index) => (
            <div
              key={item.title}
              className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 animate-slide-up hover:-translate-y-2"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <div className={`w-10 h-10 sm:w-12 sm:h-12 ${item.color} rounded-xl flex items-center justify-center text-white mb-3 sm:mb-4`}>
                <item.icon className="h-5 w-5 sm:h-6 sm:w-6" />
              </div>
              <div className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">{item.stat}</div>
              <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
              <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">
                {item.description}
              </p>
            </div>
          ))}
        </div>

        {/* Why Choose Us */}
        <div className="bg-white rounded-xl sm:rounded-3xl p-6 sm:p-8 md:p-12 shadow-xl border border-gray-100 animate-slide-up [animation-delay:400ms]">
          <div className="text-center mb-8 sm:mb-12">
            <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3 sm:mb-4">{t('performance.whyChoose.title')}</h3>
            <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto px-4">
              {t('performance.whyChoose.description')}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-8">
            {reasons.map((reason, index) => (
              <div
                key={reason.title}
                className="text-center p-4 sm:p-6 rounded-xl hover:bg-gray-50 transition-colors duration-300"
              >
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-csl-green/10 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6 text-csl-green">
                  <reason.icon className="h-6 w-6 sm:h-8 sm:w-8" />
                </div>
                <h4 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 sm:mb-3">{reason.title}</h4>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                  {reason.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Performance;